import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useCustomerData } from 'src/content/pages/customer/customerData';
import { useStyles } from 'src/theme/vemigoCustomStyles/VemigoCustomStyles';
import { enqueueSnackbar } from 'notistack';
import { useSalesPartnerData } from '../../SalesPartnerData';
import { SalesPartnerPaymentModel } from 'src/models/salesPartner/SalesPartnerPaymentModel';
import { SalesPartnerPaymentServices } from 'src/services/salesPartner/salesPartnerPaymentServices';

const paymentMethodType = [
  { id: 0, name: 'Online' },
  { id: 1, name: 'Cash' }
];

const EditPaymentHistory = (props) => {
  const { bankAccountTypes } = useCustomerData();
  const { salesPartner, bankAccountsList } = useSalesPartnerData();
  const { open, handleClose, selectedPayment } = props;

  const [newData, setNewData] = useState({
    date: '',
    paymentMode: null,
    bankAccount: null,
    amount: '',
    paidBy: '',
    paidTo: '',
    voucherNumber: '',
    additionalNotes: '',
  });
 
  const [showField, setShowField] = useState(false);

  
  useEffect(() => {
    if (selectedPayment) {
     
      const formattedDate = selectedPayment.date ? selectedPayment.date.split('T')[0] : '';
      setShowField(selectedPayment.spPaymentMode === 1);

      setNewData({
        date: formattedDate,  
        paymentMode: selectedPayment.spPaymentMode,
        bankAccount: selectedPayment.spBankAccountId ?? null,
        amount: selectedPayment.amount || '',
        paidBy: selectedPayment.paidByName || '',
        paidTo: selectedPayment.receivedByName || '',
        voucherNumber: selectedPayment.voucherNumber || '',
        additionalNotes: selectedPayment.note || '',
      });

    }
  }, [selectedPayment]);


  const handleDataChange = (fieldName, value) => {
    setNewData((prevData) => ({
      ...prevData,
      [fieldName]: value
    }));
  };

  const handleDataUpdate = () => {
    const updatedData: SalesPartnerPaymentModel = {
      id: selectedPayment?.id ?? 0,
      date: newData.date,
      paymentMethod: newData.paymentMode,
      spBankAccountId: newData.bankAccount || 0,
      amount: parseFloat(newData.amount) || 0,
      paidById: 0,
      paidByName: newData.paidBy,
      receivedById: 0,
      receivedByName: newData.paidTo,
      voucherNumber: newData.voucherNumber,
      note: newData.additionalNotes || '',
      salesPartnerId: salesPartner?.id || 0,
    };

    SalesPartnerPaymentServices.updateData(updatedData)
      .then((data) => {
        enqueueSnackbar(
          `Payment Details '${data.date} ${paymentMethodType.find((item) => item.id === data.paymentMethod)?.name}' updated successfully`,
          { variant: 'success' }
        );
        handleClose();
        props.onEditSuccess(data);  
      })
      .catch(() => {
        enqueueSnackbar(
          `Payment Details update failed`,
          { variant: 'error' }
        );
      });
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle style={useStyles.dialogTitle}>Edit Payment Details</DialogTitle>
      <DialogContent style={{ ...useStyles.bodyStyle, ...useStyles.boxSize }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="h5" style={useStyles.formTitle}>Select Date *</Typography>
            <TextField
              type="date"
              fullWidth
              value={newData.date || ''}
              onChange={(e) => handleDataChange('date', e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h5" style={useStyles.formTitle}>Payment Mode *</Typography>
            <Autocomplete
              options={paymentMethodType}
              getOptionLabel={(option) => option.name}
              fullWidth
              value={
                paymentMethodType.find((x) => x.id === newData.paymentMode) ?? null
              }
              onChange={(event, newValue) => {
                if (newValue) {
                  handleDataChange('paymentMode', newValue.id);
                  setShowField(newValue.id === 1); 
                }
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder="Select Payment Mode" />
              )}
            />

          </Grid>

          {showField ? (
            <Grid item xs={6}>
              <Typography variant="h5" style={useStyles.formTitle}>Paid To *</Typography>
              <TextField
                fullWidth
                value={newData.paidTo || ''}
                onChange={(e) => handleDataChange('paidTo', e.target.value)}
              />
            </Grid>
          ) : (
            <Grid item xs={6}>
              <Typography variant="h5" style={useStyles.formTitle}>Select Bank Account *</Typography>
              <Autocomplete
                options={bankAccountsList}
                getOptionLabel={(option) => option.bankName}
                fullWidth
                value={bankAccountsList.find((x) => x.id === newData.bankAccount) || null}
                onChange={(event, newValue) => handleDataChange('bankAccount', newValue ? newValue.id : null)}
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select Bank Account" />}
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <Typography variant="h5" style={useStyles.formTitle}>Amount *</Typography>
            <TextField
              fullWidth
              value={newData.amount || ''}
              onChange={(e) => handleDataChange('amount', e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h5" style={useStyles.formTitle}>Paid By *</Typography>
            <TextField
              fullWidth
              value={newData.paidBy || ''}
              onChange={(e) => handleDataChange('paidBy', e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h5" style={useStyles.formTitle}>Voucher Number *</Typography>
            <TextField
              fullWidth
              value={newData.voucherNumber || ''}
              onChange={(e) => handleDataChange('voucherNumber', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" style={useStyles.formTitle}>Additional Notes</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={newData.additionalNotes || ''}
              onChange={(e) => handleDataChange('additionalNotes', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleClose}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleDataUpdate}>Update</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPaymentHistory;